import { ErrorPanel, InfoCard, Progress } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { createFrontendPlugin } from '@backstage/frontend-plugin-api';
import { EntityCardBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Link, List, ListItem, ListItemText } from '@material-ui/core';
import { useEffect, useState } from 'react';

const GITHUB_PROJECT_SLUG = 'github.com/project-slug';

type GitHubRelease = {
  id: number;
  html_url: string;
  name: string | null;
  published_at: string | null;
  tag_name: string;
};

function GithubReleasesCard() {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const projectSlug = entity.metadata.annotations?.[GITHUB_PROJECT_SLUG];
  const [releases, setReleases] = useState<GitHubRelease[]>();
  const [error, setError] = useState<Error>();
  const componentName = entity.metadata.title ?? entity.metadata.name;

  useEffect(() => {
    if (!projectSlug) {
      return undefined;
    }

    let cancelled = false;

    async function loadReleases() {
      try {
        const slug = projectSlug;
        if (!slug) {
          return;
        }

        const [owner, repo] = slug.split('/');
        if (!owner || !repo || slug.split('/').length !== 2) {
          throw new Error('Invalid GitHub project slug.');
        }

        const baseUrl = await discoveryApi.getBaseUrl('github-releases');
        const response = await fetchApi.fetch(
          `${baseUrl}/releases/${encodeURIComponent(owner)}/${encodeURIComponent(
            repo,
          )}`,
          {
            headers: {
              Accept: 'application/vnd.github+json',
            },
          },
        );

        if (!response.ok) {
          throw new Error(
            `GitHub returned ${response.status} while loading releases.`,
          );
        }

        if (!cancelled) {
          setReleases((await response.json()) as GitHubRelease[]);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(
            loadError instanceof Error
              ? loadError
              : new Error('Could not load GitHub releases.'),
          );
        }
      }
    }

    loadReleases();
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi, projectSlug]);

  if (error) {
    return <ErrorPanel error={error} title="GitHub releases unavailable" />;
  }

  if (!releases) {
    return <Progress />;
  }

  return (
    <InfoCard
      title={`${componentName} GitHub releases`}
      deepLink={{
        link: `https://github.com/${projectSlug}/releases`,
        title: 'View all releases',
      }}
    >
      {releases.length === 0 ? (
        'No published GitHub releases.'
      ) : (
        <List dense>
          {releases.map(release => (
            <ListItem key={release.id} divider>
              <ListItemText
                primary={
                  <Link
                    href={release.html_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {release.name || release.tag_name}
                  </Link>
                }
                secondary={
                  release.published_at
                    ? `${release.tag_name} · ${new Date(
                        release.published_at,
                      ).toLocaleDateString()}`
                    : release.tag_name
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </InfoCard>
  );
}

const githubReleasesCard = EntityCardBlueprint.make({
  name: 'releases',
  params: {
    filter: entity =>
      Boolean(entity.metadata.annotations?.[GITHUB_PROJECT_SLUG]),
    loader: async () => <GithubReleasesCard />,
  },
});

export const githubReleasesPlugin = createFrontendPlugin({
  pluginId: 'github-releases',
  extensions: [githubReleasesCard],
});
